import { startWith, debounceTime, tap, switchMap, distinctUntilChanged, filter } from 'rxjs/operators';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ElementRef
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject, Observable, of } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { IDropdown } from '../dropdown-interface';
import { Icons } from '@vex/icons/icons';
import { PopoverService } from '@vex/components/popover/popover/popover.service';
import { PopoverTooltipComponent } from '@vex/components/popover/popover-tooltip/popover-tooltip.component';
import { RefreshSelectionOptionService } from '@vex/services/refresh-selection-option.service';
@Component({
  selector: 'vex-single-selection',
  templateUrl: './single-selection.component.html',
  styleUrls: ['./single-selection.component.scss']
})
export class SingleSelectionComponent extends Icons implements OnInit, AfterViewInit, OnDestroy {
  setting: any = {};
  attributeControl: any = {};
  /** list of value */
  options: IDropdown[] = [];
  /** Prevents clearing an edit value before its asynchronous option list arrives. */
  private selectionValuesLoaded = false;
  @Input() panelClass: string;

  /** control for the selected form */
  public formCtrl: FormControl = new FormControl();

  /** control for the MatSelect filter keyword */
  public formFilterCtrl: FormControl = new FormControl();

  /** list of values filtered by search keyword */
  public filteredValues: ReplaySubject<IDropdown[]> = new ReplaySubject<IDropdown[]>(1);

  @ViewChild('singleSelect', { static: true }) singleSelect: MatSelect;

  /** Subject that emits when the component has been destroyed. */
  _onDestroy = new Subject<void>();

  input$ = new Subject<string>();
  optionSelected = false;

  @Input() set data(value: any) {
    this.setting = value || {};
  }

  @Input() set selectionValues(value: any) {
    this.options = value ? value : [];
    this.selectionValuesLoaded = true;
    // load the initial value list
    this.filteredValues.next(this.options?.slice());
    this.filteredData = this.options?.slice();
    if(this.setting.IsMandatory && !this.setting.groupOptions && this.options.length == 1) {
      this.formCtrl.setValue(this.options[0].Value);
      this.setValue(this.options[0].Value);
    }
    this.clearSelectionWhenMissing();
  }
  @Input() set form(form: any) {
    // set initial selection
    this.attributeControl = form;
    this.formCtrl.setValue(form?.value[this.setting.Name]);
    this.clearSelectionWhenMissing();
  }
  /** Enables opt-in clearing when a persisted selection no longer exists. */
  private shouldClearMissingSelection = false;
  /** Clears an unavailable persisted value after both the form and options are ready. */
  @Input() set clearMissingSelection(value: boolean) {
    this.shouldClearMissingSelection = value === true;
    this.clearSelectionWhenMissing();
  }
  @Input() isDisable: boolean = false;
  @Input() isShowRefreshButton: boolean = true;
  @Input() isLableColorWhite: boolean = false;
  @Input() allowCustomOption: boolean = false;
  @Input() isParentMandatory: boolean = true;
  @Input() service;
  @Input() fetchOnSearch: boolean = false;
  @Input() fetchMoreOnScrollEnd: boolean = false;
  @Input() filterKey: string = '';
  @Input() filter: any = {
    Filter: '',
    PageNumber: 0,
    PageSize: 10
  };
  @Output() convertDataUrl = new EventEmitter();
  @Output() viewFullScreen = new EventEmitter();
  @Output() selectionChange = new EventEmitter();
  @Output() onChangeGroupSetting = new EventEmitter();
  @Output() createAppComponent = new EventEmitter();
  @Output() onEdit = new EventEmitter();

  filteredData: any;
  pageIndex: number = 0;
  totalData: number = 0;
  minLengthTerm = 1;

  constructor(
    private cd: ChangeDetectorRef,
    private popover: PopoverService,
    private refreshSelectionOptionService: RefreshSelectionOptionService
  ) {
    super();
    this.refreshSelectionOptionService.refreshData.pipe(takeUntil(this._onDestroy)).subscribe((x) => {
      this.onClickRefresh();
    });
  }

  onChangeValue(val) {
    if (val == '@') {
      this.selectionChange.emit("@");
      this.onChangeGroupSetting.emit();
    }
  }

  getDataAttribute(action, label) {
    return action + ' ' + label;
  }

  ngOnInit() {
    // listen for search field value changes
    if (this.fetchOnSearch) {
      this.onSearch();
      this.input$.next('');

      // Link the search control to the Subject
      this.formFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((searchText) => {
        if (!this.optionSelected) {
          this.input$.next(searchText);
        }
      });
    } else {
      this.formFilterCtrl.valueChanges
        .pipe(
          takeUntil(this._onDestroy),
          startWith<string>(''),
          distinctUntilChanged(),
          debounceTime(400)
        )
        .subscribe(() => {
          this.filterValues();
        });
    }
  }

  ngAfterViewInit() {
    let searchInput = document.querySelector('#singleSelectSearch input') as HTMLInputElement;
    if (searchInput) searchInput.focus();

    this.setInitialValue();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  registerPanelScrollEvent() {
    const panel = this.singleSelect.panel?.nativeElement;
    panel?.addEventListener('scroll', (event) => this.loadAllOnScroll(event));
  }

  loadAllOnScroll(event) {
    const element = event.target;

    // Detect when user reaches the bottom of the mat-select options list
    const atBottom = element.scrollHeight - element.scrollTop < element.clientHeight + 150;
    if (atBottom && this.fetchMoreOnScrollEnd && !this.setting.loader) {
      this.fetchMore();
    }
  }

  fetchMore() {
    if (this.filteredData.length >= this.totalData) {
      return;
    }
    this.setting.loader = true;
    this.pageIndex++;
    this.filter.PageNumber = this.pageIndex;
    this.service.getForSelectPaginated(this.filter).pipe(takeUntil(this._onDestroy)).subscribe(
      (result) => {
        this.filteredData = this.filteredData.concat(result.items);
        this.filteredValues.next(this.filteredData);
        this.setting.loader = false;
      },
      (error) => {
        this.setting.loader = false;
      }
    );
  }

  onSearch() {
    this.input$
      .pipe(
        takeUntil(this._onDestroy),
        distinctUntilChanged(),
        debounceTime(400),
        tap((value) => {
          this.pageIndex = 0;
          this.totalData = 0;
          this.filter.Filter = value || null;
          this.filter.PageNumber = this.pageIndex;
          this.filter[this.filterKey] = this.formCtrl.value ? [this.formCtrl.value] : [];
          this.setting.loader = true;
        }),
        switchMap((value) => this.service.getForSelectPaginated(this.filter))
      )
      .subscribe(
        (data: any) => {
          this.filteredData = data.selectedItems || [];
          this.filteredData = this.filteredData.concat(data.items);
          this.options = this.filteredData;
          this.filteredValues.next(this.filteredData);
          this.totalData = data.totalCount;
          this.setting.loader = false;
        },
        (error) => {
          this.setting.loader = false;
        }
      );
  }

  onMatSelectOpened() {
    this.optionSelected = false;
    this.registerPanelScrollEvent();
    this.input$.next('');
  }

  onMatSelectClosed() {
    this.optionSelected = true;
  }

  /**
   * Sets the initial value after the filteredValues are loaded initially
   */
  setInitialValue() {
    this.singleSelect.compareWith = (a: any, b: any) => {
      if (a === b) return true;
      let valueA = a && typeof a === 'object' ? a.Value : a;
      let valueB = b && typeof b === 'object' ? b.Value : b;
      
      return valueA !== undefined && valueB !== undefined && valueA === valueB;
    };
  }

  isJSON(str) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  filterValues() {
    if (!this.options) return;
    let search = this.formFilterCtrl.value;
    
    if (this.isJSON(this.setting.DataUrl) && !this.optionSelected) {
      this.setting['searchData'] = search;
      this.onClickRefresh();
    }

    if (!search) {
      this.filteredValues.next(this.options?.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    if (!this.setting.groupOptions) {
      let filtered = this.options.filter(opt => 
        opt.Label?.toLowerCase().includes(search)
      );

      if (this.allowCustomOption && !filtered.some(opt => opt.Label?.toLowerCase() === search)) {
        filtered.unshift({ Label: this.formFilterCtrl.value, Value: this.formFilterCtrl.value });
      }

      this.filteredValues.next(filtered);
    } else {
      let filterOptions: any[] = JSON.parse(JSON.stringify(this.options));
      for (let i = 0; i < filterOptions.length; i++) {
        filterOptions[i].Value = filterOptions[i].Value.filter((x) =>
          x.Label?.toLowerCase().includes(search)
        );
      }
      if (this.allowCustomOption) {
        filterOptions.unshift({
          Label: 'Search Result',
          Value: [
            { Label: this.formFilterCtrl.value, Value: this.formFilterCtrl.value }
          ]
        });
      }

      this.filteredValues.next(filterOptions);
    }
  }

  isCurrentValueInList(): boolean {
    let currentValue = this.formCtrl.value;
    if (!currentValue || !this.filteredData) return false;

    if (this.setting.groupOptions) {
      return this.filteredData.some(group => 
        group.Value && Array.isArray(group.Value) && group.Value.some(val => val.Value === currentValue)
      );
    } else {
      return this.filteredData.some(opt => this.optionMatchesValue(opt.Value, currentValue));
    }
  }

  /** Clears a stale value only for consumers that explicitly request this behavior. */
  private clearSelectionWhenMissing() {
    const currentValue = this.formCtrl?.value;
    if (
      !this.shouldClearMissingSelection ||
      !currentValue ||
      !this.selectionValuesLoaded ||
      this.setting?.loader ||
      !this.options
    ) {
      return;
    }
    const exists = this.setting.groupOptions
      ? this.options.some(
          (group) =>
            Array.isArray(group.Value) &&
            group.Value.some((option) => option.Value === currentValue)
        )
      : this.options.some(
          (option) => this.optionMatchesValue(option.Value, currentValue)
        );
    if (!exists) {
      this.formCtrl.setValue(null);
      this.attributeControl.get(this.setting.Name)?.setValue(null);
      this.attributeControl.get(this.setting.Name)?.markAsDirty();
    }
  }

  /** Compares primitive, nested dropdown, and grouped option value shapes safely. */
  private optionMatchesValue(optionValue: unknown, currentValue: unknown): boolean {
    const normalizedCurrentValue = this.unwrapDropdownValue(currentValue);

    if (Array.isArray(optionValue)) {
      return optionValue.some(
        (option) => this.unwrapDropdownValue(option) === normalizedCurrentValue
      );
    }

    return this.unwrapDropdownValue(optionValue) === normalizedCurrentValue;
  }

  /** Extracts a dropdown's primitive Value while leaving primitive inputs unchanged. */
  private unwrapDropdownValue(value: unknown): unknown {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      return (value as IDropdown).Value;
    }

    return value;
  }

  getSelectedLabelFallback(): string {
    const currentValue = this.formCtrl.value;
    if (!currentValue) return '';
    if (this.options) {
      if (this.setting.groupOptions) {
        for (const group of this.options) {
          if (group.Value && Array.isArray(group.Value)) {
            const found = (group.Value as any[]).find(val => val.Value === currentValue);
            if (found) return found.Label;
          }
        }
      } else {
        const found = this.options.find(opt => opt.Value === currentValue);
        if (found) return found.Label;
      }
    }
    return currentValue?.Label || currentValue;
  }

  isNoneShow() {
    let status = true;
    if (this.options) {
      status = this.options.findIndex((value) => value.Label?.toString()?.toLowerCase() == 'none') == -1;
    } else {
      status = false;
    }
    return status;
  }

  onClickRefresh() {
    this.convertDataUrl.emit();
  }

  onViewFullScreen() {
    this.viewFullScreen.emit();
  }

  setValue(value: any) {
    let actualValue = value;
    if (value?.Value && value?.Label) {
      actualValue = value.Value || value;
    }
    let oldValue = this.attributeControl.get(this.setting.Name)?.value;
    if (oldValue !== actualValue) {
      this.attributeControl.get(this.setting.Name)?.markAsDirty();
    }
    this.attributeControl.get(this.setting.Name)?.setValue(actualValue);
    this.optionSelected = true;
    if (oldValue !== actualValue) {
      this.selectionChange.emit(actualValue);
    }
  }

  isShowError() {
    let status: boolean = false;
    if (this.attributeControl?.get(this.setting.Name)) {
      if (!this.attributeControl?.get(this.setting.Name)?.valid) {
        if (!this.isParentMandatory) {
          status = this.attributeControl.get(this.setting.Name)?.dirty;
        } else {
          status =
            this.attributeControl.get(this.setting.Name)?.dirty ||
            this.attributeControl.get(this.setting.Name)?.touched;
        }
      }
    }
    return status;
  }

  onToggleSelect(event) {
    if (!event) {
      this.attributeControl.get(this.setting.Name)?.markAsTouched();
    }
  }

  showInfo(origin: ElementRef | HTMLElement, text: string) {
    this.popover.open({
      content: PopoverTooltipComponent,
      origin,
      data: { heading: 'Instructions', text: text },
      position: [
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom'
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top'
        }
      ]
    });
  }

  onClickAddButton() {
    if(this.setting.ShowAddButton) {
      this.createAppComponent.emit(this.setting);
    }
    if(this.setting.ShowEditButton) {
      this.onEdit.emit();
    }
  }
}
